
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import { Svg, Path } from 'react-native-svg';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';

const INTEREST_OPTIONS = [
  'Abstinência',
  'Adultos 60+',
  'Álcool',
  'Ansiedade',
  'Apadrinhamento',
  'Autocompaixão',
  'Autoconhecimento',
  'Autocontrole',
  'Bariátrica',
  'Burnout',
  'Compras Compulsivas',
  'Comunicação',
  'Dependência Digital',
  'Depressão',
  'Drogas / Psicoativos',
  'Emoções',
  'Estresse',
  'Família',
  'Feminilidade',
  'Gatilhos',
  'Internação',
  'Jogos',
  'Juventude',
  'LGBT+',
  'Limites',
  'Lucidez',
  'Masculinidade',
  'Maternidade',
  'Neurodivergência',
  'Novos na Recuperação',
  'Outras Dependências',
  'Paternidade',
  'Pornografia',
  'Prevenção de Recaída',
  'Psiquiatria & Medicação',
  'Raiva',
  'Recomeços',
  'Rede de Apoio',
  'Redes Sociais',
  'Relacionamentos',
  'Ressaca',
  'Rotina de Sobriedade',
  'Sexo',
  'Sobriedade Longa',
  'Solidão',
  'Terapia',
  'Transtornos Alimentares',
  'Tratamento',
  'Vergonha & Culpa',
  'Vida Sexual Saudável',
];

export default function OnboardingInterestsScreen() {
  const { logCompleted } = useOnboardingStepTracking('interests');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Load previously saved data on mount and dismiss keyboard
  useEffect(() => {
    Keyboard.dismiss();
    const loadSavedData = async () => {
      const data = await TempOnboardingService.getData();
      if (data.name) {
        setUserName(data.name);
        console.log('📥 Loaded user name:', data.name);
      }
      if (data.interests) {
        setSelectedInterests(data.interests);
        console.log('📥 Loaded previously saved interests:', data.interests);
      }
    };
    loadSavedData();
  }, []);

  const handleInterestToggle = (interest: string) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) {
        return prev.filter(item => item !== interest);
      } else {
        return [...prev, interest];
      }
    });
  };

  const handleContinue = async () => {
    if (selectedInterests.length < 3) {
      Alert.alert(t('onboarding.interests.insufficient'), t('onboarding.interests.insufficientMessage'));
      return;
    }

    setLoading(true);

    try {
      // Save interests to temporary storage (pre-auth flow)
      await TempOnboardingService.saveInterests(selectedInterests);
      console.log('✅ Interests saved to temporary storage (pre-auth flow):', selectedInterests);

      // PRD §5.2 — `onboarding_step_completed` for the `interests` step.
      logCompleted();

      // Navigate to next onboarding step (sponsorship)
      router.push('/onboarding-sponsorship');
    } catch (error) {
      console.error('❌ Error saving interests:', error);
      Alert.alert(t('onboarding.interests.error'), t('onboarding.interests.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const renderInterestPill = (interest: string) => {
    const isSelected = selectedInterests.includes(interest);
    
    return (
      <TouchableOpacity
        key={interest}
        style={[
          styles.pill,
          isSelected ? styles.pillSelected : styles.pillUnselected
        ]}
        onPress={() => handleInterestToggle(interest)}
      >
        <View style={styles.pillIcon}>
          {isSelected ? (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M12.6673 4.2915L6.25065 10.7082L3.33398 7.7915" 
                stroke="#015480" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          ) : (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M4 8H12" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
              <Path 
                d="M8 4V12" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          )}
        </View>
        <Text style={[
          styles.pillText,
          isSelected ? styles.pillTextSelected : styles.pillTextUnselected
        ]}>
          {interest}
        </Text>
      </TouchableOpacity>
    );
  };

  const progressWidth = 24; // Step 2 of 7

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <View style={styles.contentContainer}>
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Step Section */}
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>2</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>
                {userName ? `${userName}, ${t('onboarding.interests.titleWithName')}` : t('onboarding.interests.title')}
              </Text>
              <Text style={styles.subtitle}>
                {t('onboarding.interests.subtitle')}
              </Text>
            </View>
          </View>

          {/* Interests Section */}
          <View style={styles.interestsSection}>
            <View style={styles.pillsContainer}>
              {INTEREST_OPTIONS.map(renderInterestPill)}
            </View>
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={loading || selectedInterests.length < 3}
          />
        </View>
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: 100,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  interestsSection: {
    padding: 24,
  },
  pillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-start',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 12,
    borderRadius: 17,
    borderWidth: 1,
    gap: 4,
  },
  pillSelected: {
    backgroundColor: '#E6EBF0',
    borderColor: '#015480',
  },
  pillUnselected: {
    backgroundColor: '#FFFFFF',
    borderColor: '#DAD8CF',
  },
  pillIcon: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pillText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16.8,
  },
  pillTextSelected: {
    color: '#015480',
  },
  pillTextUnselected: {
    color: '#151515',
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
